import { useCallback, useEffect, useRef, useState } from "react";
import { mediaStreamManager } from "../audio/mediaStreamManager";
import {
  listAudioDevices,
  subscribeDeviceChange,
  type AudioDeviceLists,
} from "../audio/deviceEnumerator";
import { TTS_PHRASES } from "../audio/ttsPhrases";
import { getTtsPlayer } from "../audio/ttsPlayerInstance";
import { detectVoiceSupport } from "../audio/voiceSupport";
import { encodeWavPcm16 } from "../audio/wavEncoder";
import { AppError } from "../errors/types";
import { eventBus } from "../observability/eventBus";
import { makeEvent } from "../observability/eventSchema";
import { useChatStore } from "../state/chatStore";
import { identityProvider, sessionUploader } from "../storage/sessionUploaderInstance";
import { usePreflightVoice, type DroppedSegment } from "../hooks/usePreflightVoice";
import { MicLevelMeter } from "./preflight/MicLevelMeter";
import { DeviceSelect } from "./preflight/DeviceSelect";
import {
  NameCapture,
  type NameEscalationReason,
  type NameRankedInfo,
  type NameSecondAsrInfo,
} from "./preflight/NameCapture";
import { SpeakerCheck, type SpeakerStats } from "./preflight/SpeakerCheck";
import { Tutorial } from "./preflight/Tutorial";
import { PreflightStage } from "./preflight/PreflightStage";

// 事前チェック wizard (spec §4.3/§4.4)。許可 → スピーカー → マイク+氏名 (D13 統合) → 遊び方 (柱A) → 完了 (D15 順序)。
// mic stream は mediaStreamManager singleton に載せたまま onReady するので、
// ChatScreen の continuous.start() は権限ダイアログなしで再利用できる (spec §4.6)。
export type PreflightStep =
  | "intro"
  | "request-mic"
  | "mic-denied"
  | "speaker-check"
  | "name-input"
  | "tutorial"
  | "ready";

export type PreflightResult = {
  // spec D10: onReady 到達 = マイク許可済み (micGranted は持たない)
  displayName?: string;
  inputDeviceId?: string;
  outputDeviceId?: string;
};

type Props = {
  micSupported: boolean;
  // bootstrap 済み session の userId (App が 1 度だけ解決したもの、spec 2026-07-29 §4.4)。
  // **ここで identityProvider から解決し直さない** — AnonymousIdentityProvider は
  // localStorage が使えない環境では呼ぶたびに別の UUID を返すので、解決し直すと
  // 氏名の音声だけが events.jsonl / manifest と違う user prefix に 1 本ずつ散る。
  // 他の保存 (events / manifest / turn audio) はすべて bootstrap 値で揃っている。
  userId: string;
  onReady: (result: PreflightResult) => void;
};

// ready step (spec 2026-07-19 D19)。読み上げ文言は TTS_PHRASES.ready (ttsPhrases.ts への
// 集約、2026-07-26)。**画面には出さない** — 2026-08-05 の D15 で完了案内も「マイク 1 つ」に
// したため、この文は耳にしか届かない。
// TTS 合成・再生の失敗は voice.speak() が即 resolve するため (原則 5)、それだけで遷移すると
// 案内が一瞬で通り過ぎる。最低これだけは置いてから進む (Tutorial の MIN_ON_SCREEN_MS と
// 同じ役割の床。画面から文字が消えた後も、合成が全滅したときの保険として要る)。
const READY_MIN_DISPLAY_MS = 1500;

// NameCapture の key (Fix 1, spec D13 準拠)。usePreflightVoice は [voice] の参照が
// 安定しているため、NameCapture の聴取 effect は入力デバイス切替では再発火しない
// (listen() は呼び出し時点の stream ref を読むだけで、切替を検知して自ら
// 再リスニングする仕組みを持たない)。そのため入力デバイス id を key にして
// NameCapture ごと remount させ、切替のたびに「認識を abort → 新 stream で
// 自動再リスニング」を強制する。副作用として attempts/failures カウンタも
// remount のたびにリセットされるが、切替前の試行は既に手放したデバイスに対する
// ものなので、これは正直な計測になる (旧 stream に向かって喋り続けタイムアウトを
// 待つだけの「試行」を数え続けるより望ましい)。
const NAME_CAPTURE_DEFAULT_KEY = "default-input";

// preflight.* emit (r2-log-expansion の既存挿入点と同じ sessionId gate パターン。
// App が Preflight mount 前に startSession({sessionId}) 済みのため通常は必ず通過する)
function emitPreflight(type: string, payload: Record<string, unknown>): void {
  const sessionId = useChatStore.getState().sessionId;
  if (!sessionId) return;
  eventBus.emit(makeEvent(type, sessionId, payload));
}

// spec 2026-08-22 D9。回り込みが酷い端末で events.jsonl を埋めないための上限。
const MAX_NAME_SEGMENT_DROPS = 10;

// 列挙の失敗は devicechange のたびに繰り返しうるので上限を置く。
const MAX_DEVICE_ENUM_FAILURES = 3;

const STEP_INDEX: Record<PreflightStep, number> = {
  intro: 0,
  "request-mic": 0,
  "mic-denied": 0,
  "speaker-check": 1,
  "name-input": 2,
  tutorial: 3,
  ready: 4,
};
const STEP_TOTAL = 5;

export function Preflight({ micSupported, userId, onReady }: Props) {
  const [step, setStep] = useState<PreflightStep>("intro");
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [micErrorMessage, setMicErrorMessage] = useState("");
  const [devices, setDevices] = useState<AudioDeviceLists>({ inputs: [], outputs: [] });
  const [inputDeviceId, setInputDeviceId] = useState<string | null>(null);
  const [outputDeviceId, setOutputDeviceId] = useState<string | null>(null);
  const [deviceErrorMessage, setDeviceErrorMessage] = useState("");
  const [displayName, setDisplayName] = useState<string | null>(null);
  const startedAtRef = useRef<number>(performance.now());
  const completedRef = useRef(false);
  // D24: 今の氏名 step で弾いた合計回数 (preflight.name_captured.rejections)。
  // NameCapture 側の rejectionsRef は「連続」カウンタで別物 — こちらは合計。
  // リセット契機は下の nameCaptureKey effect (NameCapture の mount と同じ単位で数え直す)。
  const nameRejectionsRef = useRef(0);
  // 誰のものでもないセグメントを記録した件数 (spec 2026-08-22 D9)。**step あたり 10 件で
  // 打ち切る** — 読み上げの回り込みが酷い端末では際限なく出うる。**打ち止め自体は
  // 記録しない** (打ち止めイベントが 11 件目になる)。
  const nameSegmentDropsRef = useRef(0);
  // 列挙が失敗した回数 (2026-08-26)。**頻度が未知なのでまず数える** — 0 件なら
  // 「失敗そのものを直す」必要が無かったという結論になり、それはそれで正しい答え。
  const deviceEnumFailuresRef = useRef(0);
  // D26-9: 対処モード (「聞こえないときは」) を通ったか。**session 単位**の事実なので
  // SpeakerCheck ではなく wizard 側が持つ — 「← 戻る」で入り直すと SpeakerCheck は
  // 再 mount され component 内の state/ref は初期化されるため、そこに持たせると
  // 通った人が「通っていない」と記録され、spec §8.4 の差分計算が偽陽性を出す。
  // nameRejectionsRef と同じ作法。session をまたがないのでリセットはしない。
  const speakerTroubleshotRef = useRef(false);
  // 氏名発話の通番 (spec 2026-07-29 §3.3)。R2 のキー
  // users/{uid}/sessions/{sid}/preflight/name-{index}.wav は **session 単位**なので、
  // 通番も session 単位で持つ。NameCapture 側 (mount 単位) に置くと、入力デバイス切替や
  // tutorial / ready からの「← 戻る」で remount されるたびに 0 から振り直され、
  // 先に保存した音声が黙って上書きされる (しかも events.jsonl には同じ index が 2 度
  // 並ぶので、§3.4 の読み方が別の発話のテキストをその音声に紐づける)。
  // speakerTroubleshotRef と同じ作法だが、**nameRejectionsRef と違い nameCaptureKey の
  // effect ではリセットしない** — 揃えるべき相手が attempts (mount 単位) ではなく
  // R2 のキー (session 単位) だから。
  const nameAudioIndexRef = useRef(0);

  // wizard 全体で TtsPlayer を 1 つだけ持つ (spec 2026-07-19 §5.1)。stream/outputDeviceId
  // は毎レンダー最新値を渡すだけでよい (hook 内部が ref で追従する、Task 3 参照)。
  // 誰のものでもないセグメントを記録する (spec 2026-08-22 D9)。**app.error は呼ばない** —
  // あれは critical かつ 10 件/session で、氏名 step だけで使い切りうる (段 3 の D21 と同じ理由)。
  const handleSegmentDropped = useCallback((info: DroppedSegment) => {
    if (nameSegmentDropsRef.current >= MAX_NAME_SEGMENT_DROPS) return;
    nameSegmentDropsRef.current += 1;
    emitPreflight("preflight.name_segment_dropped", { ...info });
  }, []);
  const voice = usePreflightVoice({
    stream,
    outputDeviceId: outputDeviceId ?? undefined,
    // **氏名 step のあいだだけ VAD を生かす** (spec 2026-08-22 D2)。stream も deps に
    // 入っているので、入力デバイスを切り替えると古い VAD を壊してから新しい stream で
    // 起動し直す (順序は React の cleanup 規則が保証する)。
    micOpen: step === "name-input",
    onSegmentDropped: handleSegmentDropped,
  });

  // NameCapture の mount 識別子。name-input step でだけ描画され (下の JSX)、入力デバイスを
  // 切り替えると値が変わって key として NameCapture を remount させる。null = 未マウント。
  const nameCaptureKey = step === "name-input" ? (inputDeviceId ?? NAME_CAPTURE_DEFAULT_KEY) : null;

  useEffect(() => {
    // 端末とビューポートをここで載せる。**`userAgent` 単独なら #78 と重複する** —
    // あちらが manifest の書き込みをアンケートを開いた時点へ前倒ししたので、
    // 事前チェックで離脱した人 (パイロットでは「最初の画面だけ」10 件) の端末は
    // manifest からも数えられるようになった。ここが単独で持つのは 3 つ:
    // (1) `viewport` (manifest には無く、`session.started` は本編突入で初めて出る)
    // (2) `?device=skip` で判定を飛ばした回の理由
    // (3) manifest の POST だけが失敗して events が残った回の保険
    //    (admin は #79 で `no_manifest` を状態として持つようになった)
    // Chrome-iOS で音声が VAD に届かない件 (B3) は、まずその分母が要る。
    //
    // 読むのは effect の中で行う (module top の const にしない) — テストが差し替え
    // られるようにするため。`?scribe-debug=1` / `resolveSttProvider()` と同じ流儀。
    //
    // `voiceSupported` の真偽値は載せない: 非対応端末は session を採番しないので
    // (spec 2026-08-04 D6) ここに来る時点で常に true = 情報量がゼロ。代わりに
    // `?device=skip` で判定を飛ばして通した回だけ理由が載る。
    const support = detectVoiceSupport();
    emitPreflight("preflight.started", {
      userAgent: navigator.userAgent,
      viewport: { w: window.innerWidth, h: window.innerHeight },
      ...(support.supported ? {} : { voiceUnsupportedReason: support.reason }),
    });
  }, []);
  useEffect(() => {
    emitPreflight("preflight.step_viewed", { step });
  }, [step]);
  // D24: 弾いた合計回数は NameCapture が mount / remount される契機にそろえて 0 に戻す
  // (spec 2026-07-25 §7)。NameCapture 側の attempts / 連続カウンタは mount 単位で数え直す
  // ため、合計だけを step 往復や入力デバイス切替をまたいで持ち越すと、
  // preflight.name_captured に rejections > attempts という起こり得ない組が載る
  // (「1 回しか話していないのに 2 回弾かれた」)。判定 precision の集計はこの 2 値の比で
  // 見るので、スコープを一致させておく。
  useEffect(() => {
    if (nameCaptureKey === null) return; // NameCapture が居ない step では触らない
    nameRejectionsRef.current = 0;
    // 落としたセグメントの上限も同じ単位で数え直す (step 往復・入力デバイス切替で仕切り直す)。
    nameSegmentDropsRef.current = 0;
  }, [nameCaptureKey]);

  const refreshDevices = useCallback(async () => {
    // 列挙失敗はマイク許可の可否と無関係 (レビュー指摘: permission denial と
    // 混同されないよう、ここで握り潰して空リストへ degrade する。既存 UI は
    // 空リストでも「既定のデバイス」表示に自然にフォールバックする)。
    try {
      setDevices(await listAudioDevices());
    } catch {
      // **握り潰すが、黙って消さない (2026-08-26)。** ここが空リストへ倒れると
      // 「出力先を選び直してください」の枝 (D26-5) が消え、**まさにそれを必要と
      // している人 (音が聞こえない人) に対して**助言が出なくなる。頻度が分からない
      // 限り直し方も決められないので、まず件数を残す。
      if (deviceEnumFailuresRef.current < MAX_DEVICE_ENUM_FAILURES) {
        deviceEnumFailuresRef.current += 1;
        emitPreflight("preflight.device_enumeration_failed", {
          attempt: deviceEnumFailuresRef.current,
        });
      }
      setDevices({ inputs: [], outputs: [] });
    }
  }, []);

  // BT イヤホン抜き差し等で一覧を更新 (spec §4.4 Step 2)
  useEffect(() => subscribeDeviceChange(() => void refreshDevices()), [refreshDevices]);

  // **選んだデバイスが一覧から消えたら選択を捨てる (2026-08-26)。**
  //
  // これが無いと **「既定のデバイスへ戻せない」片道の罠**になる。React の
  // `updateOptions` は `value` に一致する option が無いと**最初の非 disabled option**を
  // 選ぶので、`DeviceSelect` が必ず先頭に出す「既定のデバイス」が DOM 上は選択される。
  // 一方 state は消えた deviceId を持ったままで、**画面と state が食い違う**。
  // その状態で「既定のデバイス」を選んでも `value` が変わらないので `change` が発火せず、
  // `onSelect(null)` に到達できない。適用側 (`usePreflightVoice` / `TtsPlayer`) は
  // どちらも `.catch(() => undefined)` なので、**無音のまま画面は正常に見える**。
  //
  // **列挙の失敗を必要としない。** BT を抜く / スリープから復帰する、で同じ状態になる。
  // だから「失敗したら前回値を保つ」という直し方は**逆効果**で、消えたデバイスを
  // 選択肢に残し続けることで食い違いが表面化する契機まで奪う。
  useEffect(() => {
    setOutputDeviceId((id) =>
      id !== null && !devices.outputs.some((d) => d.deviceId === id) ? null : id
    );
    setInputDeviceId((id) =>
      id !== null && !devices.inputs.some((d) => d.deviceId === id) ? null : id
    );
  }, [devices]);

  const complete = (result: PreflightResult) => {
    if (completedRef.current) return;
    completedRef.current = true;
    emitPreflight("preflight.completed", {
      durationMs: Math.round(performance.now() - startedAtRef.current),
    });
    onReady(result);
  };

  // ready effect (下記) から使う。displayName 等の state は ready 表示中は変化しない前提
  // だが、常に最新のレンダーの値で complete() を呼べるよう毎レンダー ref を更新しておく
  // (usePreflightVoice / SpeakerCheck / NameCapture と同じ ref 経由の作法)。
  const completeRef = useRef<() => void>(() => undefined);
  completeRef.current = () => {
    complete({
      displayName: displayName ?? undefined,
      inputDeviceId: inputDeviceId ?? undefined,
      outputDeviceId: outputDeviceId ?? undefined,
    });
  };

  // ready step: 読み上げてから自動で本編へ進む (spec 2026-07-19 D19)。D15 で
  // 「アンケートを始める」ボタンを畳んだので、**本編へ進む経路はここだけ**になった
  // (completedRef の二重実行 guard は、cleanup と競合する経路に対する番人として残す)。
  useEffect(() => {
    if (step !== "ready") return;
    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    const shownAt = performance.now();
    void (async () => {
      await voice.speak(TTS_PHRASES.ready);
      if (cancelled) return;
      const wait = READY_MIN_DISPLAY_MS - (performance.now() - shownAt);
      if (wait > 0) {
        await new Promise<void>((resolve) => {
          timeoutId = setTimeout(resolve, wait);
        });
      }
      if (cancelled) return;
      completeRef.current();
    })();
    return () => {
      cancelled = true;
      if (timeoutId !== undefined) clearTimeout(timeoutId);
      // 表示中に戻る等で離脱したら読み上げ中の音声を止める (旧 stopSpeakerTest の役割を
      // 引き継ぐ箇所。voice.speak() 自体は合成中でも cancel() の世代カウンタで
      // 遅れて届いた再生を無効化できる、Task 3 参照)
      voice.cancel();
    };
  }, [step, voice]);

  const handleBegin = async () => {
    // iOS (WebKit) の再生アンロックはユーザー操作のコールスタックの中でしか得られない。
    // この関数は最初の await までタップと同じ実行文脈で走るので、**必ず先頭で**呼ぶ。
    // 下の await より後ろに置くと文脈が切れ、以降の読み上げが 1 つも鳴らなくなる
    // (docs/measurements/2026-08-04-ios-webkit-audio-diagnosis.md の T2/T3)。
    // wizard で確実に得られるユーザー操作はこのタップだけなので、ここが唯一の機会。
    getTtsPlayer().unlock();
    if (!micSupported) {
      setMicErrorMessage(
        "このブラウザは音声入力に対応していません。PC の Chrome など対応ブラウザで開き直してください。"
      );
      setStep("mic-denied");
      return;
    }
    setStep("request-mic");
    try {
      const s = await mediaStreamManager.ensureStream();
      setStream(s);
      emitPreflight("preflight.mic_permission", { granted: true });
      await refreshDevices(); // 権限付与後なら label が取れる (spec §4.4 Step 1)
      setStep("speaker-check"); // D15: 許可の直後にスピーカー確認 (以降の音声案内が届く前提を先に作る)
    } catch (e) {
      emitPreflight("preflight.mic_permission", { granted: false });
      // spec D10: 拒否は受け付けない。再許可手順を案内しリトライのみ提示する
      setMicErrorMessage(
        e instanceof AppError && e.code === "mic-permission-denied"
          ? "マイクの利用が許可されませんでした。このアンケートは音声でお答えいただくため、マイクの許可をお願いしています。お手数ですが、アドレスバーのマイクアイコン (サイト設定) から「許可」に変更し、「もう一度試す」を押してください。"
          : "マイクが見つかりませんでした。お手数ですが、マイクの接続を確認して「もう一度試す」を押してください。"
      );
      setStep("mic-denied");
    }
  };

  const handleSelectInput = async (deviceId: string | null) => {
    setDeviceErrorMessage("");
    try {
      const next = await mediaStreamManager.switchDevice(deviceId);
      setStream(next); // MicLevelMeter は re-attach
      // NameCapture は key={inputDeviceId} を持つため、この setInputDeviceId で
      // remount され、その effect が新 stream で自動的に再リスニングを開始する
      // (usePreflightVoice は [voice] の参照が安定しているため、prop の変化だけでは
      // 聴取 effect が再発火しない — Fix 1、D13 準拠)
      setInputDeviceId(deviceId);
      emitPreflight("preflight.device_selected", {
        kind: "audioinput",
        changedFromDefault: deviceId !== null,
      });
    } catch {
      // switchDevice は取得成功後にしか差し替えないため旧 stream が生きている
      setDeviceErrorMessage(
        "選択したマイクに切り替えられませんでした。別のデバイスをお試しください。"
      );
    }
  };

  const handleSelectOutput = (deviceId: string | null) => {
    setOutputDeviceId(deviceId);
    emitPreflight("preflight.device_selected", {
      kind: "audiooutput",
      changedFromDefault: deviceId !== null,
    });
    // sinkId の適用は usePreflightVoice が outputDeviceId の変化を見て行う。
    // トーン再生中の作り直しは SpeakerCheck 自身の責務 (Task 4)。
  };

  // D25: この step はボタン専用 (method は常に "button")。playCycles = ボタンを押すまでに
  // 鳴らしたテスト音のサイクル数。旧 attempts (はい/いいえ聴取の試行回数) とは意味が
  // 違うため、同じ名前を使い回さず改名してある (D25-4、原則 6)。
  // D26-6: replays = 本人の操作で鳴らし直した回数。自動 reprompt の回数は
  // playCycles - replays - 1 で復元できる (spec §8.3)。
  // troubleshot は session 単位の ref から載せる (D26-9)。
  const handleSpeakerHeard = (method: "button", stats: SpeakerStats) => {
    emitPreflight("preflight.response", {
      step: "speaker-check",
      method,
      playCycles: stats.playCycles,
      replays: stats.replays,
      troubleshot: speakerTroubleshotRef.current,
    });
    setStep("name-input"); // 合計カウンタのリセットは nameCaptureKey の effect が行う (D24)
  };

  // D26-6: 対処モードに入った = 聞こえないと申告した。この 1 件が無いと
  // 「聞こえなかった人が居たか」が events.jsonl に残らない (原則 6)。
  // response{troubleshot:true} との差が「対処モードから出られなかった人」になる
  // (読み方と成立条件は spec §8.4 — session 単位で数えること)。
  const handleSpeakerUnheard = (stats: SpeakerStats) => {
    speakerTroubleshotRef.current = true;
    emitPreflight("preflight.speaker_unheard", {
      playCycles: stats.playCycles,
      replays: stats.replays,
    });
  };

  // D24: 非氏名と判定して聞き直したときの計測。consecutive === 2 のイベントが
  // 1 つでもあれば fail-open (判定を降ろした) と後から判別できる (一方通行のため)。
  // 弾いた raw transcript はここに載せない — scribe.final として events.jsonl に既にあり、
  // タイムスタンプで突合できる (重複保存の回避)。
  // **union から partial を外した (spec 2026-08-19 D5)。** partial は却下されなくなったので
  // preflight.name_rejected.verdict にはもう載らない。回数は name_captured.partials が持つ。
  const handleNameRejected = (
    verdict: "refusal" | "unrelated",
    attempt: number,
    consecutive: number
  ) => {
    nameRejectionsRef.current += 1;
    emitPreflight("preflight.name_rejected", { verdict, attempt, consecutive });
  };

  const handleNameResponded = (method: "voice" | "button" | "keyboard") => {
    // attempts は **意図的に載せない**。NameCapture の onRespond は method しか運ばず
    // (「いいえ」で氏名認識からやり直す構造のため、SpeakerCheck のような独立した
    // 「はい/いいえ聴取の試行回数」を持たない)、ここで固定値 1 を入れると集計時に
    // 「本当に 1 回で成功した」ケースと区別がつかなくなる。本 PoC は計測データ自体が
    // 成果物なので (CLAUDE.md 原則 6)、値を捏造するよりフィールドを欠落させて
    // 「未計測」であることを正直に示す。氏名認識の試行回数は preflight.name_captured
    // が別途持つ。将来 onRespond を widen する場合はここに実測値を載せる
    // (spec 2026-07-19 §6、レビュー指摘の反映)。
    emitPreflight("preflight.response", { step: "name-input", method });
  };

  // D25-3 / D25-7: 収束しないと判定して退避画面へ落とした。従来なら無限ループだった
  // 場面を救えたことを示す。reason は実質 "no-progress" のみが発火する — D25-5
  // (逐次確認) 導入後、"same-result" は構造的に到達不能になった (NameCapture.tsx の
  // prior 分岐のコメント参照)。スキーマには番人として値が残っているだけなので、
  // 解析側は "no-progress" を収束不能の唯一の実測信号として扱うこと (spec §7)。
  const handleNameEscalated = (reason: NameEscalationReason, recognitions: number) => {
    emitPreflight("preflight.name_escalated", { reason, recognitions });
  };

  // D25-4: 音では決まらない表記の差 (ヅ/ズ・長音の書き分け) を 2 択で決めた。
  // この差が実機でどのくらい起きるかは未測定で、1 件も出なければ 2 択自体が
  // 不要だったと判断できる (spec §10)。described は差を口で言える形に説明できたか。
  const handleNameSpellingChoice = (
    chosen: "first" | "second",
    method: "voice" | "button",
    described: boolean
  ) => {
    emitPreflight("preflight.name_spelling_choice", { chosen, method, described });
  };

  // D25-4: 2 択を「出した」瞬間の計測。choice (解決したときだけ発火) とは非対称にし、
  // 「どちらでもありません」・曖昧応答の打ち切り・STT error で抜けた場合でも 2 択を
  // 提示した事実は残す。choice だけを見ると「2 択が発火しているのに 0 件」と誤読し
  // D25-4 自体が不要だったと判断されかねないため (spec §7)。
  const handleNameSpellingPrompted = (described: boolean) => {
    emitPreflight("preflight.name_spelling_prompted", { described });
  };

  // D25-5: 逐次確認 (同じ step で 2 人目以降の候補を続けて確かめる) が実際に候補を
  // 提示した通知。spec §4.2 で「本リポでは退化する」と予告した枝なので、発火しない
  // ことを確認するためにこそ計測が要る (name_spelling_prompted と同じ論理、spec §7)。
  const handleNameFollowupPresented = (index: number) => {
    emitPreflight("preflight.name_followup_presented", { index });
  };

  // 氏名の発話を R2 に保存し、対応付けの目印を残す (spec 2026-07-29 D5)。
  // **氏名認識で捕まえた発話はすべて**保存する (D3: 弾かれた分・聞き取れなかった分こそが
  // 「なぜ外したか」の測定対象)。復唱確認の「はい/いいえ」は askYesNo が onSpeechEnd を
  // 渡さないので自然に外れる (D4)。
  //
  // 保存は fire-and-forget。sessionId が無い / WAV encode に失敗する / upload が 3 回とも
  // 落ちる のどの経路でも氏名確認の体験は変えない (原則 5 / D7、§3.5)。**この関数は
  // throw しない** — NameCapture は recognizeSingleUtterance の onSpeechEnd から同期で
  // これを呼ぶので、throw すると Scribe への commit ごと落ちて認識が timeout する。
  // **戻り値はその回の通番 (spec 2026-08-20 §5.1)。** 第 2 経路と順位付けのイベントは
  // 同じ番号で emit するので、NameCapture 側に番号を渡す手段がこれしかない (通番は
  // session 単位でここが持ち、NameCapture は remount されるため向こうで振れない)。
  // **保存を見送った回 (sessionId が無い) も返す** — 通番は既に進めてあるので、
  // 返さないと enrich が番号を失う (戻り値の型を number にしてあるので、
  // 早期 return を void に戻すと typecheck が落ちる)。
  const handleNameAudio = (audio: Float32Array): number => {
    // 通番は保存を見送った回も進める — index は「氏名認識を何回回したか」で、
    // 同じキーを二度使わないことが優先。
    const index = nameAudioIndexRef.current;
    nameAudioIndexRef.current = index + 1;
    const sessionId = useChatStore.getState().sessionId;
    if (!sessionId) return index;
    try {
      sessionUploader.enqueuePreflightAudio({
        userId,
        sessionId,
        index,
        blob: encodeWavPcm16(audio),
      });
      // 音声とテキストの対応付けはこのイベントの位置で読む (spec §3.4): 直後の
      // scribe.final がこの音声のテキストで、現れなければその回は STT が失敗している。
      // **enqueue の後**に emit する — 音声が無いのに目印だけ残ると、解析側がその回を
      // 「STT が失敗した (音声はあるがテキストが無い)」と読み違える。**間に await を
      // 挟まない**ので、Scribe の確定より必ず先に並ぶ。
      emitPreflight("preflight.name_audio", { index });
    } catch {
      // encode / enqueue / emit のいずれで落ちても氏名確認は止めない (§3.5)
    }
    return index;
  };

  // 第 2 経路 (ElevenLabs batch STT) を撃った結果 (spec 2026-08-20 §5.2)。
  // **index は preflight.name_audio と同じ通番**で、音声・Scribe の確定文と突き合わせる。
  //
  // **text を載せる** — 同じ音声の Scribe 側の書き起こしは scribe.final に載っているので、
  // 第 2 経路が何を返したかが残らないと「第 2 経路が効いたか」を後から数えられない
  // (段 0〜段 2 の再解析はすべて保存済み WAV を再送して作った)。R2 の同じ bucket に
  // 同じ音声と同じ氏名が既にある以上、新しい種類の情報は増えていない。
  //
  // **scribe.final に混ぜてはいけない** — あれは CRITICAL_TYPES で「発話テキストの正」
  // であり、別モデル・別プロバイダの文字列を同じ型に入れると (a) provider の欠落 =
  // elevenlabs realtime という既存の読み方が壊れ、(b) 発話数の集計が二重になる。
  // **CRITICAL_TYPES にも入れない** — critical を増やすと trimForBeacon が縮められない
  // 床が上がり、48KB を超えた最終バッチが丸ごと落ちる。
  const handleNameSecondAsr = (info: NameSecondAsrInfo) => {
    emitPreflight("preflight.name_second_asr", {
      index: info.index,
      ms: info.ms,
      outcome: info.outcome,
      // 取れなかった回に空文字を入れると「上流が空を返した」と区別できない (§5.2)
      ...(info.text !== undefined ? { text: info.text } : {}),
    });
  };

  // 順位付け (LLM) を撃った結果 (§5.3 / D27)。**撃った回は失敗でも必ず残る** —
  // これが無いと、name-rank が系統的に 400 を返す状態 (prompt に "JSON" 語が要る
  // contract 違反 = 2026-07-21 に本番で 1 度出している型) になっても、ログには
  // returned: 0 かイベントの欠落しか残らず「LLM が候補を出せなかった」と区別できない。
  // D21 で reportAppError を降ろした以上、**ここが唯一の観測点**である。
  const handleNameRanked = (info: NameRankedInfo) => {
    emitPreflight("preflight.name_ranked", {
      index: info.index,
      ms: info.ms,
      paths: info.paths,
      returned: info.returned,
      ranked: info.ranked,
      novel: info.novel,
      outcome: info.outcome,
    });
  };

  const handleNameCaptured = (
    name: string,
    info: {
      attempts: number;
      recognitions: number;
      partials: number;
      confirmNarratedMs?: number;
      // spec 2026-08-20 §5.4: 確定した候補の出所と、その回の fresh で何番目だったか。
      candidateSource?: "ranked" | "llm" | "asr";
      candidateRank?: number;
      method: "voice" | "keyboard";
    }
  ) => {
    setDisplayName(name);
    // D21: 氏名の入力手段 (voice/keyboard) を計測に載せる。最後の手段 (keyboard) の
    // 利用率を測るため (spec §4.9)。schemaVersion は 1 据え置き (payload は緩い record)。
    // D24: rejections はこの step で非氏名と判定して弾いた合計回数 (spec 2026-07-25 §7)。
    // D25: recognitions は認識に成功した回数。attempts は非氏名判定や STT 失敗も含む
    // 通算値なので、「何回の発話で確定したか」は別フィールドで持つ (spec 2026-07-26 §7)。
    emitPreflight("preflight.name_captured", {
      attempts: info.attempts,
      recognitions: info.recognitions,
      method: info.method,
      rejections: nameRejectionsRef.current,
      // spec 2026-08-19 §5: 判定器が partial を返した回数。**rejections の意味が狭まった**
      // (これまでは name 以外すべて = partial を含んでいたが、今後は unrelated / refusal
      // だけ) ので、失われる分をここに出して `rejections + partials` で旧 rejections と
      // 同じ軸に戻せるようにする。同名のまま意味だけ変えると集計時に気づけない
      // (D26-4 の attempts → playCycles で一度踏んだ)。
      partials: info.partials,
      // 2026-08-20: 確定した復唱の読み上げにかかった時間。**「はい」を読み終わりまで
      // 押せなくした代償 (本人を待たせた時間) がこれで測れる。** 復唱を経ない経路
      // (キーボード) では undefined なので、payload ごと省いて「未計測」を表す
      // (0 を入れると「待たせなかった」と区別できなくなる)。
      ...(info.confirmNarratedMs !== undefined
        ? { confirmNarratedMs: info.confirmNarratedMs }
        : {}),
      // spec 2026-08-20 §5.4: 確定した候補の出所と順位。**「1 位がそのまま採用された回」と
      // 「いいえ で 2 位に進んだ回」がこの 2 つで読める。** 候補ではない経路 (キーボード) と
      // 1 つの数で書けない経路 (表記の 2 択の rank) は **key ごと省く** —
      // confirmNarratedMs と同じ扱いで、0 や null を入れると再解析側が
      // 「順位付けの 1 位で確定した」と読める値になってしまう。
      ...(info.candidateSource !== undefined ? { candidateSource: info.candidateSource } : {}),
      ...(info.candidateRank !== undefined ? { candidateRank: info.candidateRank } : {}),
    });
    // 永続は fire-and-forget (原則 5)。将来の Google login 差し替えは IdentityProvider 抽象側
    void identityProvider.setDisplayName(name).catch(() => undefined);
    // 氏名確認の後、本編に入る前に遊び方チュートリアル (柱A) を挟む。ここまでで音が届くことは
    // 確認済みなので、ナレーションと合図音の試聴が成立する (D15 の「音が届く前提を先に作る」)。
    setStep("tutorial");
  };

  const handleTutorialDone = () => {
    emitPreflight("preflight.tutorial_viewed", {});
    setStep("ready"); // 遊び方の共有が済んだので、あとは完了案内のみ
  };

  // 全 step に出る戻り導線。実寸 43.2x16.5 と HIG のタップ領域 44x44 に**幅も高さも**
  // 届いていなかったので、min-h-[44px] で高さを、px-1 → px-3 で幅を確保する
  // (43.2 - 8 + 24 = 59px)。周囲は gap-4 (16px) で離れており負 margin で節約する必要が
  // ないため実寸で取る。文字は 13px (HIG の Footnote 相当) — 11px は HIG の最小
  // (Caption 2) だがあれはキャプションや注釈の下限で、ボタンのラベルに使う値ではない。
  // 主動線の CTA との差は塗りつぶしの有無と文字色 (slate) が担うので、サイズで下げない。
  const backButton = (to: PreflightStep) => (
    <button
      type="button"
      onClick={() => setStep(to)}
      className="self-start inline-flex items-center min-h-[44px] px-3 text-[13px] text-slate-500 hover:text-slate-700 hover:underline"
    >
      ← 戻る
    </button>
  );

  return (
    <div className="mx-auto w-full max-w-2xl flex flex-col flex-1 min-h-0 bg-slate-50 overflow-y-auto">
      <header className="shrink-0 bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between">
        <h1 className="text-sm font-bold text-slate-900">はじめる前の確認</h1>
        <div className="flex items-center gap-1">
          {Array.from({ length: STEP_TOTAL }, (_, i) => (
            <span
              key={i}
              className={`w-4 h-0.5 rounded ${i <= STEP_INDEX[step] ? "bg-indigo-600" : "bg-slate-200"}`}
            />
          ))}
        </div>
      </header>
      <div className="flex-1 px-4 py-6 flex flex-col gap-4">
        {step === "intro" && (
          <>
            <p className="text-sm text-slate-700">
              はじめに、マイクとスピーカーの動作を確認します。このアンケートは、音声でお答えいただけます。
            </p>
            <p className="text-xs text-slate-500">
              「はじめる」を押すと、ブラウザがマイクの使用許可をおたずねします。
            </p>
            {/* px-6 py-2 text-sm だけでは高さ 36px で HIG のタップ領域 44pt に届かない。
                主動線の CTA は当たり判定が見えていること自体に価値があるので、疑似要素や
                負 margin で隠さず min-h-[44px] + inline-flex の中央揃えで実寸を取る
                (この wizard の他の CTA も同じ 1 セット)。表示テキストは 1 文字も変えない —
                E2E の getByRole({name}) と事前生成 TTS の sha256 キーが依存している。 */}
            <button
              type="button"
              onClick={() => void handleBegin()}
              className="self-center min-h-[44px] inline-flex items-center justify-center px-6 py-2 rounded-full text-sm font-semibold bg-indigo-600 text-white hover:bg-indigo-700"
            >
              はじめる
            </button>
          </>
        )}
        {step === "request-mic" && (
          <p className="text-sm text-slate-500">
            マイクの許可をお待ちしています。ブラウザに表示されたダイアログで「許可」を選んでください。
          </p>
        )}
        {step === "mic-denied" && (
          <>
            <p className="text-sm text-amber-800 bg-amber-50 border border-amber-300 rounded p-3">
              {micErrorMessage}
            </p>
            {/* spec D10: 拒否は受け付けない。本編へ進む導線は出さない */}
            {micSupported ? (
              <>
                <button
                  type="button"
                  onClick={() => void handleBegin()}
                  className="self-center min-h-[44px] inline-flex items-center justify-center px-6 py-2 rounded-full text-sm font-semibold bg-indigo-600 text-white hover:bg-indigo-700"
                >
                  もう一度試す
                </button>
                <p className="text-xs text-slate-500">
                  許可に変更しても進めない場合は、ページを再読み込みしてからもう一度お試しください。
                </p>
              </>
            ) : (
              <p className="text-xs text-slate-500">
                お使いのブラウザではマイクを利用できないため、このアンケートには回答できません。
              </p>
            )}
          </>
        )}
        {step === "speaker-check" && (
          <>
            <SpeakerCheck
              voice={voice}
              devices={devices.outputs}
              outputDeviceId={outputDeviceId}
              onSelectOutput={handleSelectOutput}
              onHeard={handleSpeakerHeard}
              onUnheard={handleSpeakerUnheard}
            />
            {backButton("intro")}
          </>
        )}
        {step === "name-input" && (
          <>
            {/* D13: マイクチェックと氏名確認の統合画面。氏名の発話がマイクテストを兼ねる */}
            <p className="text-sm text-slate-700">
              お話しになると、声に合わせて緑のバーが動きます。動かないときは、マイクを選び直してください。
            </p>
            <MicLevelMeter stream={stream} />
            <DeviceSelect
              label="マイク"
              devices={devices.inputs}
              selectedId={inputDeviceId}
              onSelect={(id) => void handleSelectInput(id)}
            />
            {deviceErrorMessage && <p className="text-xs text-amber-700">{deviceErrorMessage}</p>}
            <NameCapture
              key={nameCaptureKey}
              voice={voice}
              onConfirmed={handleNameCaptured}
              onRespond={handleNameResponded}
              onRejected={handleNameRejected}
              onEscalated={handleNameEscalated}
              onSpellingChoice={handleNameSpellingChoice}
              onSpellingPrompted={handleNameSpellingPrompted}
              onFollowupPresented={handleNameFollowupPresented}
              onNameAudio={handleNameAudio}
              onSecondAsr={handleNameSecondAsr}
              onRanked={handleNameRanked}
            />
            {backButton("speaker-check")}
          </>
        )}
        {step === "tutorial" && (
          <>
            <Tutorial voice={voice} onDone={handleTutorialDone} />
            {backButton("name-input")}
          </>
        )}
        {step === "ready" && (
          <>
            {/* D15 (2026-08-05): 遊び方チュートリアル・本編前半と同じ「マイク 1 つ」。
                読み上げている文 (TTS_PHRASES.ready) も、設定サマリも、「アンケートを始める」
                ボタンも出さない — D14 と D2 の後、**この 1 step だけが読ませる画面として
                取り残され**、声だけで通してきた流れが本編に入る直前で途切れていた。
                サマリ (お名前 / マイク / スピーカー) を畳めるのは、3 つとも既に別の手段で
                確かめてあるから: スピーカーは「聞こえました」を押した時点 (D25)、マイクは
                氏名を声で通した時点、氏名は復唱確認 (「〜さんですね」) で。しかも読み上げ +
                1.5 秒で自動的に進むので、そもそも読んで確かめる時間が無い。氏名の取り違えに
                気づいた人の復帰路は「← 戻る」が引き続き担う。 */}
            <PreflightStage testId="preflight-ready" />
            {backButton("name-input")}
          </>
        )}
      </div>
    </div>
  );
}
